import type { RouteObject } from 'react-router-dom';

import { StubPage } from '@/app/pages/StubPage';
import { defineHandle } from '@/app/routes/types';

import { catalogAccessRoles } from './access';

/**
 * All `/catalog/*` routes for composition into `app/router.tsx`.
 */
export const catalogRoutes: RouteObject[] = [
  {
    path: 'catalog',
    element: <StubPage titleKey="nav.catalog" dataPage="catalog/index" />,
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalog',
    }),
  },
  {
    path: 'catalog/banner/new',
    async lazy() {
      const { BannerFormPage } = await import('./banners/pages/BannerFormPage');
      return { Component: BannerFormPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogBanner',
    }),
  },
  {
    path: 'catalog/banner/:bannerId/edit',
    async lazy() {
      const { BannerFormPage } = await import('./banners/pages/BannerFormPage');
      return { Component: BannerFormPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogBanner',
    }),
  },
  {
    path: 'catalog/banner',
    async lazy() {
      const { BannerListPage } = await import('./banners/pages/BannerListPage');
      return { Component: BannerListPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogBanner',
    }),
  },
  {
    path: 'catalog/kit/new',
    async lazy() {
      const { KitFormPage } = await import('./kits/pages/KitFormPage');
      return { Component: KitFormPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogKitNew',
    }),
  },
  {
    path: 'catalog/kit/:kitId/edit',
    async lazy() {
      const { KitFormPage } = await import('./kits/pages/KitFormPage');
      return { Component: KitFormPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogKit',
    }),
  },
  {
    path: 'catalog/kit/:kitId',
    async lazy() {
      const { KitDetailPage } = await import('./kits/pages/KitDetailPage');
      return { Component: KitDetailPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogKit',
    }),
  },
  {
    path: 'catalog/kit',
    async lazy() {
      const { KitListPage } = await import('./kits/pages/KitListPage');
      return { Component: KitListPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogKit',
    }),
  },
  {
    path: 'catalog/brand',
    async lazy() {
      const { BrandPage } = await import('./brands/pages/BrandPage');
      return { Component: BrandPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogBrand',
    }),
  },
  {
    path: 'catalog/category',
    async lazy() {
      const { CategoryPage } = await import('./categories/pages/CategoryPage');
      return { Component: CategoryPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogCategory',
    }),
  },
  {
    path: 'catalog/category/:id',
    async lazy() {
      const { CategoryDetailPage } = await import('./categories/pages/CategoryDetailPage');
      return { Component: CategoryDetailPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogCategory',
    }),
  },
  {
    path: 'catalog/category/seasonal-assignment',
    async lazy() {
      const { SeasonalAssignmentPage } = await import('./categories/pages/SeasonalAssignmentPage');
      return { Component: SeasonalAssignmentPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogCategory',
    }),
  },
  {
    path: 'catalog/product',
    async lazy() {
      const { ProductPage } = await import('./products/pages/ProductPage');
      return { Component: ProductPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogProduct',
    }),
  },
  {
    path: 'catalog/product/master/:id/edit',
    async lazy() {
      const { EditMasterProductPage } = await import('./products/pages/EditMasterProductPage');
      return { Component: EditMasterProductPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogProduct',
    }),
  },
  {
    path: 'catalog/product/master/:id',
    async lazy() {
      const { MasterProductDetailPage } = await import('./products/pages/MasterProductDetailPage');
      return { Component: MasterProductDetailPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogProduct',
    }),
  },
  {
    path: 'catalog/product/:id',
    async lazy() {
      const { ProductDetailPage } = await import('./products/pages/ProductDetailPage');
      return { Component: ProductDetailPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogProduct',
    }),
  },
  {
    path: 'catalog/product/:id/edit',
    async lazy() {
      const { EditProductPage } = await import('./products/pages/EditProductPage');
      return { Component: EditProductPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogProduct',
    }),
  },
  {
    path: 'catalog/product/new',
    async lazy() {
      const { CreateProductPage } = await import('./products/pages/CreateProductPage');
      return { Component: CreateProductPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogProductNew',
    }),
  },
  {
    path: 'catalog/product/new/barcode',
    async lazy() {
      const { ProductBarcodePage } = await import('./products/pages/ProductBarcodePage');
      return { Component: ProductBarcodePage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogProductNew',
    }),
  },
  {
    path: 'catalog/store',
    async lazy() {
      const { StoreCatalogPage } = await import('./products/pages/StoreCatalogPage');
      return { Component: StoreCatalogPage };
    },
    handle: defineHandle({
      roles: [...catalogAccessRoles],
      breadcrumb: 'breadcrumb.home',
      title: 'common:nav.catalogStore',
    }),
  },
];
